/**
 * roleStorage — clears stale role-scoped state on mode switch / logout.
 *
 * The marketplace ↔ profile loop, the frozen Quick Debrief, and the
 * sticky founder banner across reloads were all symptoms of stale
 * client-side state. This helper centralises cleanup so any future
 * mode switch (start, exit, reset, logout) calls a single function and
 * we know nothing leaks across roles.
 */

// Keys/prefixes that are role-scoped and must be wiped on a switch.
// Anything matching this list (exact key OR prefix) is removed.
const ROLE_SCOPED_PATTERNS = [
  "role",
  "founder_mode",
  "founder",
  "operating_mode",
  "selectedStudent",
  "workMode",
  "work_mode",
  "lessonTimer",
  "lesson_timer",
  "instructor_view",
  "learner_view",
  "onboarding_step",
  "onboarding_state",
  "wm_",
  "debrief",
  "recording",
  "recorder_state",
  "quick_debrief",
  "live_transcript",
  "voice_transcript",
  "active_lesson",
  "lesson_state",
  "currentBooking",
  "current_booking",
  "lesson_focus_draft",
  "lessonFocusDraft",
  "instructor_profile_draft",
  "learner_profile_draft",
];

function matchesAnyPattern(key) {
  if (!key) return false;
  const lower = key.toLowerCase();
  return ROLE_SCOPED_PATTERNS.some((p) => lower.startsWith(p.toLowerCase()) || lower.includes(p.toLowerCase()));
}

/** Wipe all role-scoped keys from BOTH localStorage and sessionStorage. */
export function clearRoleScopedStorage() {
  try {
    if (typeof window === "undefined") return;
    for (const storage of [window.localStorage, window.sessionStorage]) {
      if (!storage) continue;
      const toRemove = [];
      for (let i = 0; i < storage.length; i++) {
        const k = storage.key(i);
        if (matchesAnyPattern(k)) toRemove.push(k);
      }
      toRemove.forEach((k) => {
        try { storage.removeItem(k); } catch (e) {}
      });
    }
  } catch (e) {
    // Storage might be disabled (private mode, etc.) — never throw.
  }

  // Best-effort: stop any active media recorders / audio streams so the
  // mic doesn't stay live after a mode switch.
  try {
    if (window.__activeMediaRecorder) {
      try { window.__activeMediaRecorder.stop(); } catch (e) {}
      window.__activeMediaRecorder = null;
    }
    if (window.__activeMediaStream) {
      try {
        window.__activeMediaStream.getTracks().forEach((t) => t.stop());
      } catch (e) {}
      window.__activeMediaStream = null;
    }
    if (window.__activeSpeechRecognition) {
      try { window.__activeSpeechRecognition.abort(); } catch (e) {}
      window.__activeSpeechRecognition = null;
    }
  } catch (e) {}
}

export default clearRoleScopedStorage;
